import { createContext, type FC, type PropsWithChildren, useContext } from "react";

import { ErrorView } from "@/components/_molecules/error-view";
import { Loading } from "@/components/_molecules/loading-view";

import { type Sector } from "../models/sector-model";

import { useGetSector } from "./api/use-get-sector";

type CurrentSectorContext = {
  currentSector: Sector;
};

const CurrentSectorContext = createContext<CurrentSectorContext | null>(null);

export interface CurrentSectorContextProviderProps extends PropsWithChildren {
  sectorId: string;
  subsectorIdList?: readonly string[];
}

export const CurrentSectorContextProvider: FC<CurrentSectorContextProviderProps> = ({
  children,
  sectorId,
  subsectorIdList,
}) => {
  const { sector, loading, error } = useGetSector({ id: sectorId });

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorView />;
  }

  if (!sector) {
    return <ErrorView />;
  }

  return (
    <CurrentSectorContext.Provider
      value={{
        currentSector: subsectorIdList
          ? {
              ...sector,
              subsectorList: sector.subsectorList.filter((subsector) => subsectorIdList.includes(subsector.id)),
            }
          : sector,
      }}
    >
      {children}
    </CurrentSectorContext.Provider>
  );
};

export const useCurrentSectorContext = () => {
  const context = useContext(CurrentSectorContext);

  if (!context) {
    throw new Error("useCurrentSectorContext must be used within a CurrentSectorContextProvider");
  }

  return context;
};
